import { Component, OnDestroy, OnInit } from '@angular/core';
import { IAutoCompleteItem } from 'auto-complete-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { CONDITIONAL_FIELDS_ENTITY_NAME, CONDITIONAL_FIELDS_ENTITY_NAME_LABEL_PAIR, IFieldMeta } from '../../../interfaces/imetadata';
import { AutoCompleteDefaultService } from '../../../services/auto-complete/auto-complete.default.service';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../../../services/content-control-to-field-map.service';
import { ControllerService } from '../../../services/controller.service';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { LoggingService } from '../../../services/logging.service';
import { ServerModelService } from '../../../services/server-model.service';
import { Condition, ConditionValue } from '../../../utils/condition';
import { FieldContext } from '../../../utils/fieldcontext';
import { FieldType } from '../../../utils/fieldtype';
import { templateContext } from '../../../utils/template-context';
import { IValidStateChangeProvider, ValidStateProvider } from '../valid-state-provider';

enum Mode {
  INSERT,
  UPDATE,
}

@Component({
  selector: 'app-conditional-fields',
  templateUrl: './conditional-fields.component.html',
  styleUrls: ['./conditional-fields.component.css'],
  standalone: false,
})
export class ConditionalFieldsComponent implements OnInit, OnDestroy, IValidStateChangeProvider {
  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }
  static readonly POPULATED: string = 'populated';
  static readonly NOT_POPULATED: string = 'not populated';
  static readonly EQUALS: string = 'equals';

  static readonly Operators = [
    ConditionalFieldsComponent.POPULATED,
    ConditionalFieldsComponent.NOT_POPULATED,
    ConditionalFieldsComponent.EQUALS,
  ];

  viewFieldPath = '';
  viewFieldType = '';
  viewOperator = ConditionalFieldsComponent.POPULATED;
  viewValue: ConditionValue = '';

  mode = Mode.INSERT;

  private valid = new ValidStateProvider();
  private conditionChangeSubject = new Subject<void>();
  private conditionChangeSubscription: Subscription | undefined;
  private activeContentControlSubscription: Subscription;

  protected readonly FieldType = FieldType;
  protected readonly Mode = Mode;
  protected readonly Operators = ConditionalFieldsComponent.Operators;
  protected readonly Populated = ConditionalFieldsComponent.POPULATED;
  protected readonly NotPopulated = ConditionalFieldsComponent.NOT_POPULATED;

  constructor(
    private controllerService: ControllerService,
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private loggingService: LoggingService,
    private messageNotificationService: MessageNotificationService,
    private identityStorage: IdentityStorage,
    private autoCompleteService: AutoCompleteDefaultService,
    private serverModelService: ServerModelService
  ) {
    this.activeContentControlSubscription = this.activeContentControlMonitor.activeContentControlChanged$.subscribe({
      next: (acc) => this.activeControlChanged(acc),
    });
  }

  ngOnInit(): void {
    this.valid.setValid(true);
    // Debounce condition changes for efficient updates
    this.conditionChangeSubscription = this.conditionChangeSubject.pipe(debounceTime(300)).subscribe(() => this.updateValidity());
  }

  ngOnDestroy(): void {
    this.conditionChangeSubscription?.unsubscribe();
    this.activeContentControlSubscription?.unsubscribe();
  }

  isValid(): boolean {
    if (this.viewOperator === ConditionalFieldsComponent.POPULATED || this.viewOperator === ConditionalFieldsComponent.NOT_POPULATED) {
      return !!this.viewFieldPath;
    }
    return !!this.viewFieldPath && !!this.viewOperator && !!this.viewValue;
  }

  operatorUpdated() {
    if (this.viewOperator === ConditionalFieldsComponent.POPULATED || this.viewOperator === ConditionalFieldsComponent.NOT_POPULATED) {
      this.viewValue = '';
    }
  }

  onAddOrUpdateCondition(): void {
    const field = templateContext.getFieldByPath(this.viewFieldPath);
    const isFieldValid = !!field && field.type !== FieldType.TO_ONE && field.type !== FieldType.COMPOSITE;
    if (!isFieldValid) {
      this.messageNotificationService.error('The field you have entered is not valid.');
      return;
    }
    if (field?.parent?.type === 'composite') {
      this.serverModelService.insertPath(this.identityStorage.get().namespace, field.parent.path);
    }

    this.mode === Mode.INSERT ? this.insertConditionalField() : this.updateConditionalField();
    this.conditionChangeSubject.next(); // Trigger validity update
  }

  fieldSelected(fieldPath: string): void {
    this.viewFieldPath = fieldPath;
  }

  filterFn(filterValue: string): Observable<IAutoCompleteItem<any>[]> {
    return this.autoCompleteService.queryFn(filterValue).pipe(
      map((result) => {
        if (!filterValue || !filterValue.includes('.')) {
          // Skip empty values or values without a dot for performance reasons
          return result;
        }
        return result.filter((i) => this.shouldIncludeField(i.data));
      })
    );
  }

  tabHandlerFn(input: string, item: IAutoCompleteItem<any> | undefined): string | undefined {
    return this.autoCompleteService.tabHandlerFn(input, item);
  }

  private insertConditionalField(): void {
    Word.run(async (context) => {
      try {
        await this.controllerService.insert(context, this.buildFieldContext());
        await context.sync();
      } catch (err: any) {
        this.loggingService.error('ConditionalFieldsComponent', 'insertConditionalField', {
          message: 'Failed to insert conditional field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert conditional field.', err);
      }
    });
  }

  private updateConditionalField(): void {
    Word.run(async (context) => {
      try {
        await this.controllerService.update(context, this.buildFieldContext());
        await context.sync();
      } catch (err: any) {
        this.loggingService.error('ConditionalFieldsComponent', 'updateConditionalField', {
          message: 'Failed to update conditional field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to update conditional field.', err);
      }
    });
  }

  private buildFieldContext() {
    const field: IFieldMeta = {
      namespace: this.identityStorage.get().namespace,
      path: this.viewFieldPath,
      label: this.viewOperator,
      type: 'conditional-field',
      parent: templateContext.getFieldByPath(this.viewFieldPath)?.parent,
    };

    const fieldContext = new FieldContext(this.identityStorage.get().namespace, CONDITIONAL_FIELDS_ENTITY_NAME_LABEL_PAIR, field);

    fieldContext.conditions = [this.getViewConditionField()];

    return fieldContext;
  }

  private updateValidity(): void {
    const isValid = this.isValid();
    this.valid.setValid(isValid);
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cconditional-field - active content control changed handler', 'color:#abcdef');
    if (this.isDocusignField(activeContentControl)) {
      this.valid.setValid(false);
      return;
    }
    const activeFieldContext = activeContentControl ? this.contentControlToFieldMapService.get(activeContentControl.id) : undefined;
    const activeConditionalField = this.isConditionalField(activeFieldContext) ? activeFieldContext : undefined;
    this.mode = activeConditionalField ? Mode.UPDATE : Mode.INSERT;
    this.initialiseState(activeConditionalField?.conditions);
    this.valid.setValid(<boolean>(!activeFieldContext || activeConditionalField));
  }

  private initialiseState(srcConfig?: Condition[]) {
    const firstConfig = srcConfig && srcConfig.length > 0 ? srcConfig[0] : undefined;
    // only using a single condition for now
    this.viewFieldPath = firstConfig ? firstConfig.fieldPath : '';
    this.viewOperator = firstConfig ? firstConfig.operator : ConditionalFieldsComponent.POPULATED;
    this.viewValue = firstConfig ? firstConfig.value : '';
    this.viewFieldType = firstConfig ? firstConfig.fieldType : '';
  }

  private getViewConditionField(): Condition {
    return new Condition(this.viewFieldPath, this.viewFieldType, this.viewOperator, this.viewValue);
  }

  private isConditionalField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === CONDITIONAL_FIELDS_ENTITY_NAME;
  }

  private isDocusignField(activeContentControl?: ContentControlId): boolean {
    return activeContentControl ? activeContentControl.tag.startsWith('docusign-') : false;
  }

  private shouldIncludeField(field: IFieldMeta): boolean {
    let currentParent = field.parent;

    // Traverse up the parent hierarchy
    while (currentParent) {
      if (currentParent.type === FieldType.TO_MANY) {
        return false;
      }
      currentParent = currentParent.parent; // Move up the hierarchy
    }

    return true;
  }
}
